import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Login from './pages/Login'
import Setup from './pages/Setup'
import EventDetails from './pages/admin/EventDetails'
import AssignFacultyLeader from './pages/admin/AssignFacultyLeader'
import AssignDepartmentLeader from './pages/admin/AssignDepartmentLeader'
import AssignSubgroupLeader from './pages/admin/AssignSubgroupLeader'
import InviteLinks from './pages/admin/InviteLinks'
import ManageUserRole from './pages/admin/ManageUserRole'
import AssignToSubgroup from './pages/admin/AssignToSubgroup'
import ProtectedRoute from './components/ProtectedRoute'
import Signup from './pages/Signup'
import RequestAssignment from './pages/RequestAssignment'
import ManageRequests from './pages/admin/ManageRequests'
import AdminMemberProfile from './pages/admin/MemberProfile'
import ErrorBoundary from './components/ErrorBoundary'
import OfflineIndicator from './components/OfflineIndicator'
import LoadingSpinner from './components/LoadingSpinner'
import { ToastProvider } from './contexts/ToastContext'
import ForgotPassword from './pages/ForgotPassword.jsx'
import ResetPassword from './pages/ResetPassword.jsx'
import SubgroupLeaderUpdateInformation from './pages/mobile/subgroup-leader/SubgroupLeaderUpdateInformation'
import SubgroupLeaderUpdatePassword from './pages/mobile/subgroup-leader/SubgroupLeaderUpdatePassword'
import EventRedirect from './pages/EventRedirect'
import HierarchyPage from './pages/admin/HierarchyPage'
import AffirmationsPage from './pages/admin/AffirmationsPage'
import ReportsPage from './pages/admin/ReportsPage'
import AdminDashboard from './pages/admin/AdminDashboard'
import SettingsPage from './pages/admin/SettingsPage'
import SyncStatusNotification from './components/SyncStatusNotification'
import MemberLayout from './layouts/MemberLayout'
import SubgroupLeaderLayout from './layouts/SubgroupLeaderLayout'
import GroupLeaderLayout from './layouts/GroupLeaderLayout'
import TeamLeaderLayout from './layouts/TeamLeaderLayout'
import AdminLayout from './layouts/AdminLayout'
import LandingPage from './pages/LandingPage'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import NoMobileAccess from './pages/NoMobileAccess'
import TeamLeaderMobileHome from './pages/mobile/team-leader/TeamLeaderMobileHome'
import TeamLeaderMobileStats from './pages/mobile/team-leader/TeamLeaderMobileStats'
import TeamLeaderMobileScanner from './pages/mobile/team-leader/TeamLeaderMobileScanner'
import TeamLeaderMobileEvents from './pages/mobile/team-leader/TeamLeaderMobileEvents'
import TeamLeaderMobileEventDetails from './pages/mobile/team-leader/TeamLeaderMobileEventDetails'
import TeamLeaderMobileMembers from './pages/mobile/team-leader/TeamLeaderMobileMembers'
import TeamLeaderMobileProfile from './pages/mobile/team-leader/TeamLeaderMobileProfile'
import TeamLeaderMobileUpdatePassword from './pages/mobile/team-leader/TeamLeaderMobileUpdatePassword'
import TeamLeaderMobileUpdateInformation from './pages/mobile/team-leader/TeamLeaderMobileUpdateInformation'
import TeamLeaderMobileCode from './pages/mobile/team-leader/TeamLeaderMobileCode.jsx'
import MemberHome from './pages/mobile/member/MemberHome'
import MemberEvents from './pages/mobile/member/MemberEvents'
import MemberCode from './pages/mobile/member/MemberCode'
import MemberScanner from './pages/mobile/member/MemberScanner'
import MemberStats from './pages/mobile/member/MemberStats'
import MemberOwnProfile from './pages/mobile/member/MemberProfile'
import MemberEventDetails from './pages/mobile/member/EventDetails'
import UpdateInformation from './pages/mobile/member/UpdateInformation'
import UpdatePassword from './pages/mobile/member/UpdatePassword'
import SubgroupLeaderHome from './pages/mobile/subgroup-leader/SubgroupLeaderHome'
import SubgroupLeaderMembers from './pages/mobile/subgroup-leader/SubgroupLeaderMembers'
import SubgroupLeaderCode from './pages/mobile/subgroup-leader/SubgroupLeaderCode'
import SubgroupLeaderStats from './pages/mobile/subgroup-leader/SubgroupLeaderStats'
import SubgroupLeaderEvents from './pages/mobile/subgroup-leader/SubgroupLeaderEvents'
import SubgroupLeaderProfile from './pages/mobile/subgroup-leader/SubgroupLeaderProfile'
import SubgroupLeaderScanner from './pages/mobile/subgroup-leader/SubgroupLeaderScanner'
import AssignmentRequests from './pages/mobile/subgroup-leader/AssignmentRequests'
import SubgroupLeaderEventDetails from './pages/mobile/subgroup-leader/SubgroupLeaderEventDetails'
import GroupLeaderMemberDetail from './pages/mobile/group-leader/GroupLeaderMemberDetail'
import GroupLeaderHome from './pages/mobile/group-leader/GroupLeaderHome.jsx'
import GroupLeaderMembers from './pages/mobile/group-leader/GroupLeaderMembers'
import GroupLeaderSubgroups from './pages/mobile/group-leader/GroupLeaderSubgroups'
import GroupLeaderStats from './pages/mobile/group-leader/GroupLeaderStats'
import GroupLeaderScanner from './pages/mobile/group-leader/GroupLeaderScanner'
import GroupLeaderProfile from './pages/mobile/group-leader/GroupLeaderProfile'
import GroupLeaderUpdateInformation from './pages/mobile/group-leader/GroupLeaderUpdateInformation'
import GroupLeaderUpdatePassword from './pages/mobile/group-leader/GroupLeaderUpdatePassword'
import GroupLeaderEvents from './pages/mobile/group-leader/GroupLeaderEvents'
import GroupLeaderEventDetails from './pages/mobile/group-leader/GroupLeaderEventDetails'
import GroupLeaderCode from './pages/mobile/group-leader/GroupLeaderCode.jsx'
import SuperAdminRoute from './components/SuperAdminRoute'
import SuperAdminOrganizations from './pages/super-admin/SuperAdminOrganizations'
import OrgAdminSignup from './pages/OrgAdminSignup'

const EventsList = lazy(() => import('./pages/admin/EventsList'))
const CreateEvent = lazy(() => import('./pages/admin/CreateEvent'))
const EditEvent = lazy(() => import('./pages/admin/EditEvent'))
const MembersList = lazy(() => import('./pages/admin/MembersList'))

function App() {
  const [authReady, setAuthReady] = useState(false)

  useEffect(() => {
    // Wait for Supabase to check for existing session
    // This ensures the client is properly initialized before rendering routes
    supabase.auth.getSession().then(() => {
      setAuthReady(true)
    })
  }, [])

  // Show loading spinner while Supabase initializes (typically 50-200ms)
  // This is NOT a blank page - it's a proper loading state
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <Router>
      <ErrorBoundary>
        <ToastProvider>
          <OfflineIndicator />
          <SyncStatusNotification />
          <Routes>
           <Route path="/" element={<LandingPage />} />
           <Route path="/privacy" element={<PrivacyPolicy />} />
           <Route path="/terms" element={<TermsOfService />} />
           <Route path="/login" element={<Login />} />
           <Route path="/no-mobile-access" element={<NoMobileAccess />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/org-admin/signup/:token" element={<OrgAdminSignup />} />

            <Route path="/group-leader" element={<GroupLeaderLayout />}>
              <Route path="/group-leader/home" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderHome /></ProtectedRoute>} />
              <Route path="/group-leader/subgroups" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderSubgroups /></ProtectedRoute>} />
              <Route path="/group-leader/stats" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderStats /></ProtectedRoute>} />
              <Route path="/group-leader/scanner" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderScanner /></ProtectedRoute>} />
              <Route path="/group-leader/profile" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderProfile /></ProtectedRoute>} />
              <Route path="/group-leader/events" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderEvents /></ProtectedRoute>} />
              <Route path="/group-leader/code" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderCode /></ProtectedRoute>} />
              <Route path="/group-leader/members/:id" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderMemberDetail /></ProtectedRoute>} />
            </Route>

            <Route path="/team-leader" element={<TeamLeaderLayout />}>
              <Route path="/team-leader/home" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileHome /></ProtectedRoute>} />
              <Route path="/team-leader/stats" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileStats /></ProtectedRoute>} />
              <Route path="/team-leader/scanner" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileScanner /></ProtectedRoute>} />
              <Route path="/team-leader/events" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileEvents /></ProtectedRoute>} />
              <Route path="/team-leader/profile" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileProfile /></ProtectedRoute>} />
              <Route path="/team-leader/code" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileCode /></ProtectedRoute>} />
            </Route>

            <Route path="/member" element={<MemberLayout />}>
              <Route path="/member/home" element={<ProtectedRoute allowedLevels={['member']}><MemberHome /></ProtectedRoute>} />
              <Route path="/member/events" element={<ProtectedRoute allowedLevels={['member']}><MemberEvents /></ProtectedRoute>} />
              <Route path="/member/code" element={<ProtectedRoute allowedLevels={['member']}><MemberCode /></ProtectedRoute>} />
              <Route path="/member/scanner" element={<ProtectedRoute allowedLevels={['member']}><MemberScanner /></ProtectedRoute>} />
              <Route path="/member/stats" element={<ProtectedRoute allowedLevels={['member']}><MemberStats /></ProtectedRoute>} />
              <Route path="/member/profile" element={<ProtectedRoute allowedLevels={['member']}><MemberOwnProfile /></ProtectedRoute>} />
            </Route>

            <Route path="/subgroup-leader" element={<SubgroupLeaderLayout />}>
              <Route path="/subgroup-leader/home" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderHome /></ProtectedRoute>} />
              <Route path="/subgroup-leader/code" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderCode /></ProtectedRoute>} />
              <Route path="/subgroup-leader/scanner" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderScanner /></ProtectedRoute>} />
              <Route path="/subgroup-leader/stats" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderStats /></ProtectedRoute>} />
              <Route path="/subgroup-leader/events" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderEvents /></ProtectedRoute>} />
              <Route path="/subgroup-leader/events/:id" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderEventDetails /></ProtectedRoute>} />
              <Route path="/subgroup-leader/profile" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderProfile /></ProtectedRoute>} />
              <Route path="/subgroup-leader/assignment-requests" element={<ProtectedRoute allowedLevels={['subgroup']}><AssignmentRequests /></ProtectedRoute>} />
            </Route>

            <Route path="/admin" element={<AdminLayout />}>
            <Route path="/admin" element={<ProtectedRoute requiresOrgAdmin={true}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/hierarchy" element={<ProtectedRoute requiresOrgAdmin={true}><HierarchyPage /></ProtectedRoute>} />
            <Route path="/admin/affirmations" element={<ProtectedRoute requiresOrgAdmin={true}><AffirmationsPage /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute requiresOrgAdmin={true}><ReportsPage /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute requiresOrgAdmin={true}><SettingsPage /></ProtectedRoute>} />
            <Route path="/admin/requests" element={<ProtectedRoute requiresOrgAdmin={true}><ManageRequests /></ProtectedRoute>} />
            <Route path="/admin/members" element={<ProtectedRoute requiresOrgAdmin={true}><Suspense fallback={<LoadingSpinner message="Loading members..." />}><MembersList /></Suspense></ProtectedRoute>} />
            <Route path="/admin/members/:id" element={<ProtectedRoute requiresOrgAdmin={true}><AdminMemberProfile /></ProtectedRoute>} />
            <Route path="/admin/members/assign-faculty-leader" element={<ProtectedRoute requiresOrgAdmin={true}><AssignFacultyLeader /></ProtectedRoute>} />
            <Route path="/admin/members/assign-department-leader" element={<ProtectedRoute requiresOrgAdmin={true}><AssignDepartmentLeader /></ProtectedRoute>} />
            <Route path="/admin/members/assign-subgroup-leader" element={<ProtectedRoute requiresOrgAdmin={true}><AssignSubgroupLeader /></ProtectedRoute>} />
            <Route path="/admin/members/invite-links" element={<ProtectedRoute requiresOrgAdmin={true}><InviteLinks /></ProtectedRoute>} />
            <Route path="/admin/members/assign-to-subgroup" element={<ProtectedRoute requiresOrgAdmin={true}><AssignToSubgroup /></ProtectedRoute>} />
            <Route path="/admin/events/:id" element={<ProtectedRoute requiresOrgAdmin={true}><EventDetails /></ProtectedRoute>} />
            <Route path="/admin/events" element={<ProtectedRoute requiresOrgAdmin={true}><Suspense fallback={<LoadingSpinner message="Loading events..." />}><EventsList /></Suspense></ProtectedRoute>} />       
</Route>

            <Route path="/setup" element={<ProtectedRoute requiresOrgAdmin={true}><Setup /></ProtectedRoute>} />
            <Route path="/request-assignment" element={<ProtectedRoute><RequestAssignment /></ProtectedRoute>} />
            <Route path="/events/:id" element={<ProtectedRoute><EventRedirect /></ProtectedRoute>} />

            <Route path="/super-admin/organizations" element={<SuperAdminRoute><SuperAdminOrganizations /></SuperAdminRoute>} />

            <Route path="/admin/members/manage-role" element={<ProtectedRoute requiresOrgAdmin={true}><ManageUserRole /></ProtectedRoute>} />
            <Route path="/admin/events/create" element={<ProtectedRoute requiresOrgAdmin={true}><Suspense fallback={<LoadingSpinner message="Loading form..." />}><CreateEvent /></Suspense></ProtectedRoute>} />
            <Route path="/admin/events/:id/edit" element={<ProtectedRoute requiresOrgAdmin={true}><Suspense fallback={<LoadingSpinner message="Loading form..." />}><EditEvent /></Suspense></ProtectedRoute>} />

            <Route path="/member/profile/update-information" element={<ProtectedRoute allowedLevels={['member']}><UpdateInformation /></ProtectedRoute>} />
            <Route path="/member/profile/update-password" element={<ProtectedRoute allowedLevels={['member']}><UpdatePassword /></ProtectedRoute>} />
            <Route path="/member/events/:id" element={<ProtectedRoute allowedLevels={['member']}><MemberEventDetails /></ProtectedRoute>} />
            
            <Route path="/subgroup-leader/members" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderMembers /></ProtectedRoute>} />
            <Route path="/subgroup-leader/profile/update-information" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderUpdateInformation /></ProtectedRoute>} />
            <Route path="/subgroup-leader/profile/update-password" element={<ProtectedRoute allowedLevels={['subgroup']}><SubgroupLeaderUpdatePassword /></ProtectedRoute>} />
            
            <Route path="/group-leader/profile/update-information" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderUpdateInformation /></ProtectedRoute>} />
            <Route path="/group-leader/profile/update-password" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderUpdatePassword /></ProtectedRoute>} />
            <Route path="/group-leader/events/:id" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderEventDetails /></ProtectedRoute>} />
            <Route path="/group-leader/members" element={<ProtectedRoute allowedLevels={['group']}><GroupLeaderMembers /></ProtectedRoute>} />
            
            <Route path="/team-leader/profile/update-password" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileUpdatePassword /></ProtectedRoute>} />
            <Route path="/team-leader/profile/update-information" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileUpdateInformation /></ProtectedRoute>} />
            <Route path="/team-leader/events/:id" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileEventDetails /></ProtectedRoute>} />
            <Route path="/team-leader/members" element={<ProtectedRoute allowedLevels={['team']}><TeamLeaderMobileMembers /></ProtectedRoute>} />
          </Routes>
        </ToastProvider>
      </ErrorBoundary>
    </Router>
  )
}

export default App